import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { showSnackbar } from "../../../../../data-store/actions/organizers-actions";
import { Grid, Typography, Button } from "@material-ui/core";
import LinearProgress from "@material-ui/core/LinearProgress";
import { useTracking } from "react-tracking";

const GET_CHARGE_URL =
    "https://us-central1-kmetasfirebase.cloudfunctions.net/openpayGetChargeV1";
const FINALIZE_URL =
    "https://us-central1-kmetasfirebase.cloudfunctions.net/openpayFinalizeV1";

function getQueryParam(search, key) {
    return new URLSearchParams(search).get(key);
}

export default function OpenpayReturnInWeb(props) {
    let { order, eventDetail } = props;

    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation("inscripcion");

    const [statusText, setStatusText] = useState(t("openpay.confirmandoPago"));
    const [loading, setLoading] = useState(true);
    const [finalStatus, setFinalStatus] = useState(null); // "success" | "failed" | "timeout"
    const [attempt, setAttempt] = useState(0);

    const stoppedRef = useRef(false);

    // ✅ Contexto persistente (ya no se pier de)
    const [ctx, setCtx] = useState({
        eventoIndex: "",
        inscritoKey: "",
        chargeId: "",
    });

    const cleanupLocal = useCallback(() => {
        localStorage.removeItem("openpay_chargeId");
        localStorage.removeItem("openpay_eventoIndex");
        localStorage.removeItem("openpay_inscritoKey");
    }, []);



    // ✅ Resuelve contexto desde URL o localStorage una sola vez
    useEffect(() => {
        const eventoIndex =
            getQueryParam(location.search, "event") ||
            localStorage.getItem("openpay_eventoIndex") ||
            "";

        const inscritoKey =
            getQueryParam(location.search, "registry") ||
            localStorage.getItem("openpay_inscritoKey") ||
            "";

        const chargeId =
            getQueryParam(location.search, "chargeId") ||
            localStorage.getItem("openpay_chargeId") ||
            "";

        setCtx({ eventoIndex, inscritoKey, chargeId });
    }, [location.search]);



    const { trackEvent } = useTracking();
    function useQuery() {
        return new URLSearchParams(useLocation().search);
    }
    const query = useQuery();


    const poll = useCallback(
        async ({ eventoIndex, inscritoKey, chargeId }) => {
            const MAX_TRIES = 30;
            const INTERVAL_MS = 2000;

            for (let i = 1; i <= MAX_TRIES; i++) {
                if (stoppedRef.current) return;

                setAttempt(i);
                setStatusText(t("openpay.confirmandoPagoIntento", { intento: i, total: MAX_TRIES }));

                let status = "";
                try {
                    const r = await axios.get(GET_CHARGE_URL, {
                        params: { eventoIndex, chargeId },
                    });
                    status = r?.data?.charge?.status || "";
                } catch (e) {
                    console.error("GetCharge error", e);
                }

                const s = String(status).toLowerCase();

                // ✅ Pagado
                if (s === "completed" || s === "paid") {
                    setStatusText(t("openpay.pagoConfirmadoFinalizando"));
                    try {
                        await axios.post(FINALIZE_URL, { eventoIndex, inscritoKey, chargeId });
                        dispatch(showSnackbar(true, t("pago.pagoExitoso"), 4000));
                        cleanupLocal();
                        setFinalStatus("success");
                        setLoading(false);

                        const tkl = query.get("tkl");
                        trackEvent({
                            eventName: eventoIndex,
                            typeEvent: "Purchase",
                            params: {
                                label: "Purchase",
                                tkl: tkl || "direct",
                                order: order,
                                eventName: eventDetail.nombreEvento,
                                facebookPixel: eventDetail.facebookPixel || false,
                                gaId: eventDetail.gaId || false,
                                gateway: "openpay",
                            },
                        });

                        // ✅ Tu redirect final
                        history.replace(
                            `/${encodeURIComponent(eventoIndex)}?event=${encodeURIComponent(
                                eventoIndex
                            )}&registry=${encodeURIComponent(inscritoKey)}&download=true`
                        );
                        return;
                    } catch (e) {
                        console.error("Finalize error", e);
                        setFinalStatus("failed");
                        setLoading(false);
                        dispatch(
                            showSnackbar(
                                true,
                                t("openpay.pagoConfirmadoErrorActualizar"),
                                7000
                            )
                        );
                        return;
                    }
                }

                // ❌ Fallido/cancelado
                if (s === "failed" || s === "cancelled" || s === "canceled") {
                    setFinalStatus("failed");
                    setLoading(false);
                    setStatusText(t("openpay.pagoNoPudoCompletarse"));
                    dispatch(showSnackbar(true, t("openpay.pagoRechazadoCancelado"), 6000));
                    return;
                }

                // ⏳ Pendiente → espera
                await new Promise((r) => setTimeout(r, INTERVAL_MS));
            }

            // ⏱ Timeout
            setFinalStatus("timeout");
            setLoading(false);
            setStatusText(t("openpay.pendienteTimeout"));
        },
        [cleanupLocal, dispatch, history, t]
    );

    const start = useCallback(async () => {
        setLoading(true);
        setFinalStatus(null);
        stoppedRef.current = false;

        const { eventoIndex, inscritoKey, chargeId } = ctx;

        if (!eventoIndex || !inscritoKey || !chargeId) {
            setLoading(false);
            setFinalStatus("failed");
            dispatch(showSnackbar(true, t("openpay.faltaInformacion"), 6000));
            return;
        }

        await poll({ eventoIndex, inscritoKey, chargeId });
    }, [ctx, dispatch, poll, t]);

    // ✅ Arranca cuando ya tengas ctx completo
    useEffect(() => {
        if (!ctx.eventoIndex || !ctx.inscritoKey || !ctx.chargeId) return;

        start();
        return () => {
            stoppedRef.current = true;
        };
    }, [ctx.eventoIndex, ctx.inscritoKey, ctx.chargeId, start]);

    console.log("eventoIndex", ctx.eventoIndex);

    return (
        <Grid container direction="column" style={{ padding: 24, gap: 12 }}>
            <Typography variant="h5" color="primary">
                {statusText}
            </Typography>

            {loading && <LinearProgress />}

            <Typography variant="body2" color="textSecondary">
                {t("openpay.noCierresVentana")}
            </Typography>

            {!loading && finalStatus === "timeout" && (
                <Grid container direction="column" style={{ gap: 10, marginTop: 10 }}>
                    <Button variant="contained" color="primary" onClick={start}>
                        {t("openpay.reintentarConfirmacion")}
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => history.replace(`/${encodeURIComponent(ctx.eventoIndex || "")}?event=${encodeURIComponent(ctx.eventoIndex)}&registry=${encodeURIComponent(ctx.inscritoKey)}`)}
                        disabled={!ctx.eventoIndex}
                    >
                        {t("regresar")}
                    </Button>
                </Grid>
            )}

            {!loading && finalStatus === "failed" && (
                <Grid container direction="column" style={{ gap: 10, marginTop: 10 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => history.replace(`/${encodeURIComponent(ctx.eventoIndex || "")}?event=${encodeURIComponent(ctx.eventoIndex)}&registry=${encodeURIComponent(ctx.inscritoKey)}`)}
                        disabled={!ctx.eventoIndex}
                    >
                        {t("openpay.intentarDeNuevo")}
                    </Button>
                </Grid>
            )}
        </Grid>
    );
}
